import { Controller } from "@hotwired/stimulus"
import { highlightInvalidFields } from '../helpers/form_validation'

export default class extends Controller {
  static targets = [
    "submitButton",
    "recipientProvider",
    "recipientProviderBlock",
    "formField",
    "checkbox",
    "imageContainer",
    "withImages",
    "withoutImages",
    "form",
    "provider",
    "location",
    "locationsList",
    "yesSendButton",
    "subscriberFields",
    "welcomeBack",
    "signInLink"
  ];

  toggleSubscriber(event) {
    if (this.hasSubscriberFieldsTarget) {
      this.subscriberFieldsTarget.classList.toggle("d-none", !event.target.checked);
    }
  }

  connect() {
    this.updateSubmitButtonState();
    this.formFieldTargets.forEach(target => {
      target.addEventListener('input', () => this.updateSubmitButtonState());
    });
    this.checkboxTargets.forEach(target => {
      target.addEventListener('change', () => this.updateSubmitButtonState());
    });
    this.checkFileUploads();
    this.expanded=false
    this.setupOfficeEmailCheck();
    // A failed submission (validation 422, captcha fallback, throttle 429)
    // must restore the submit UI so the user can retry — e.g. complete the
    // reCAPTCHA v2 checkbox and send again. Besides the button labels, bring
    // back the normal "Send Referral" row (handleNo) in case the no-images
    // confirm bar was up when the failure happened.
    this.formTarget.addEventListener('turbo:submit-end', (event) => {
      if (event.detail.success) return;

      this.resetLoadingState();
      this.handleNo();
    });
  }

  // --- Returning-user detection (issue #2979, gating removed in #3007) ---
  // The office email is checked against existing accounts; a match reveals
  // the dismissible "Welcome back" sign-in banner. The form itself is never
  // locked — every field is editable at all times.

  setupOfficeEmailCheck() {
    if (!this.hasWelcomeBackTarget) return;

    this.lastCheckedEmail = null;
    this.welcomeBackDismissedFor = null;

    // Restored/prefilled email (browser back, bfcache) — check immediately.
    const emailField = this.element.querySelector('#officeEmail');
    if (emailField && this.isValidEmail(emailField.value.trim())) {
      this.checkOfficeEmail(emailField.value.trim());
    }
  }

  // "Continue without signing in" / the X — hides the banner and remembers
  // the email it was dismissed for, so re-blurring the same email doesn't
  // resurface it. A different matching email shows it again.
  dismissWelcomeBack() {
    this.welcomeBackDismissedFor = this.lastCheckedEmail;
    this.welcomeBackTarget.classList.add('d-none');
  }

  officeEmailChanged(event) {
    clearTimeout(this.officeEmailDebounce);
    const email = event.target.value.trim();
    this.officeEmailDebounce = setTimeout(() => {
      if (this.isValidEmail(email)) this.checkOfficeEmail(email);
    }, 600);
  }

  officeEmailBlurred(event) {
    clearTimeout(this.officeEmailDebounce);
    const email = event.target.value.trim();
    if (this.isValidEmail(email)) this.checkOfficeEmail(email);
  }

  isValidEmail(email) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email || '');
  }

  checkOfficeEmail(email) {
    if (email === this.lastCheckedEmail) return;
    this.lastCheckedEmail = email;

    fetch('/forms/inbound_referrals/check_office_email', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ email: email })
    })
      .then(response => response.json())
      .then(data => this.resolveOfficeEmailCheck(email, !!data.existing_account))
      .catch(() => this.resolveOfficeEmailCheck(email, false));
  }

  resolveOfficeEmailCheck(email, existingAccount) {
    // A newer check is in flight — let it drive the CTA.
    if (email !== this.lastCheckedEmail) return;
    if (!this.hasWelcomeBackTarget) return;

    if (existingAccount && email !== this.welcomeBackDismissedFor) {
      if (this.hasSignInLinkTarget) {
        const url = new URL(this.signInLinkTarget.href, window.location.origin);
        url.searchParams.set('prefill_email', email);
        this.signInLinkTarget.href = url.toString();
      }
      this.welcomeBackTarget.classList.remove('d-none');
    } else {
      this.welcomeBackTarget.classList.add('d-none');
    }
  }

  toggle(event){
    event.preventDefault();
    
    // Check if this is a location or provider toggle
    const toggleType = event.currentTarget.dataset.type || 'provider';
    const targets = toggleType === 'location' ? this.locationTargets : this.providerTargets;
    
    this.expanded = !this.expanded;
  
    targets.forEach((item, index) => {
      if (index >= 4) {
        item.classList.toggle("d-none", !this.expanded);
      }
    });
  
    event.currentTarget.textContent = this.expanded
      ? "Show less"
      : `+${targets.length - 4} more`;
  }

  showRecipientProvider() {
    this.recipientProviderTarget.value = ''
    this.recipientProviderBlockTarget.classList.remove('d-none')
  }

  hideRecipientProvider() {
    this.recipientProviderTarget.value = ''
    this.recipientProviderBlockTarget.classList.add('d-none')
  }

  updateSubmitButtonState() {
    const areAllFieldsFilled = this.formFieldTargets.every(field => field.value.trim() !== '');
    const areAllCheckboxesChecked = this.checkboxTargets.every(checkbox => checkbox.checked);

    const isAnyProviderSelected = true;

    if (isAnyProviderSelected) {
      if (areAllCheckboxesChecked) {
        this.submitButtonTarget.classList.remove('btn-lock');
      } else {
        this.submitButtonTarget.classList.add('btn-lock');
      }
    } else {
      if (areAllFieldsFilled && areAllCheckboxesChecked) {
        this.submitButtonTarget.classList.remove('btn-lock');
      } else {
        this.submitButtonTarget.classList.add('btn-lock');
      }
    }
  }

  checkFileUploads() {
    const hasFiles = this.imageContainerTarget.querySelectorAll('.file-wrapper').length > 0;

    if (hasFiles) {
      this.withImagesTarget.style.display = 'block';
      this.withoutImagesTarget.classList.add('d-none');
    } else {
      this.withImagesTarget.style.display = 'block';
      this.withoutImagesTarget.classList.add('d-none');
    }
  }

  handleSubmit(e) {
    e.preventDefault();

    if (!this.validateForm()) return;

    const hasFiles = this.imageContainerTarget.querySelectorAll('.file-wrapper').length > 0;

    // Ask the "send without images?" confirmation once per attempt — a retry
    // after a failed submission (e.g. completing the captcha fallback) submits
    // directly instead of re-asking what the user already confirmed.
    if (!hasFiles && !this.confirmedWithoutImages) {
      this.withImagesTarget.style.display = 'none';
      this.withoutImagesTarget.classList.remove('d-none');
    } else {
      this.handleYes(e)
    }
  }

  handleFileChange(e) {
    // Attachments changed — any earlier "send without images" confirmation no
    // longer describes this submission.
    this.confirmedWithoutImages = false;
    this.checkFileUploads();
  }

  handleNo(e) {
    this.withImagesTarget.style.display = 'block';
    this.withoutImagesTarget.classList.add('d-none');
  }

  setLoadingState() {
    // Disable "Send Referral" button and show loading
    if (this.hasSubmitButtonTarget) {
      this.originalSubmitButtonHtml ||= this.submitButtonTarget.innerHTML;
      this.submitButtonTarget.disabled = true;
      this.submitButtonTarget.innerHTML = '<span class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>Sending...';
    }

    // Disable "Yes, send" button and show loading
    if (this.hasYesSendButtonTarget) {
      this.originalYesSendButtonHtml ||= this.yesSendButtonTarget.innerHTML;
      this.yesSendButtonTarget.disabled = true;
      this.yesSendButtonTarget.classList.add('d-flex', 'align-items-center');
      this.yesSendButtonTarget.innerHTML = '<span class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>Sending...';
    }
  }

  resetLoadingState() {
    if (this.hasSubmitButtonTarget && this.originalSubmitButtonHtml) {
      this.submitButtonTarget.disabled = false;
      this.submitButtonTarget.innerHTML = this.originalSubmitButtonHtml;
    }
    if (this.hasYesSendButtonTarget && this.originalYesSendButtonHtml) {
      this.yesSendButtonTarget.disabled = false;
      this.yesSendButtonTarget.classList.remove('d-flex', 'align-items-center');
      this.yesSendButtonTarget.innerHTML = this.originalYesSendButtonHtml;
    }
  }

  validateForm() {
    return highlightInvalidFields(this.formTarget) === 0;
  }

  async handleYes(e) {
    if (!this.validateForm()) return;

    this.confirmedWithoutImages = true;

    // Prevent multiple submissions
    this.setLoadingState();

    await this.attachRecaptchaToken();
    this.formTarget.requestSubmit();
  }

  // Invisible reCAPTCHA Enterprise v3: fetch a token right before submit and
  // put it in the hidden field the server verifies. No-ops when bot
  // verification is off (no site-key meta / script). If the v2 checkbox
  // fallback has been swapped in by the server, its response param is what
  // matters — skip v3. On any error or timeout, submit anyway: the server
  // falls back to the v2 checkbox rather than losing the referral.
  async attachRecaptchaToken() {
    const hiddenInput = this.formTarget.querySelector('#recaptcha_enterprise_token');
    const siteKey = document.querySelector('meta[name="recaptcha-site-key"]')?.content;
    const v2CheckboxActive = this.formTarget.querySelector('textarea[name="g-recaptcha-response"]');

    if (v2CheckboxActive || !hiddenInput || !siteKey || !window.grecaptcha?.enterprise) return;

    try {
      const tokenPromise = new Promise((resolve, reject) => {
        grecaptcha.enterprise.ready(async () => {
          try {
            resolve(await grecaptcha.enterprise.execute(siteKey, { action: 'inbound_referral' }));
          } catch (err) {
            reject(err);
          }
        });
      });
      // Longer than sign-up's 1s — the iframe context can be slow, and a
      // missed token costs the user a v2 checkbox round-trip.
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('timeout')), 3000)
      );

      const token = await Promise.race([tokenPromise, timeoutPromise]);
      if (token) hiddenInput.value = token;
    } catch (err) {
      console.error('[RECAPTCHA] Error:', err.message);
    }
  }

  handleCheckboxGroupChange(event) {
    const checkbox = event.target;
    const groupName = checkbox.dataset.checkboxGroup;
    
    // Find all checkboxes in this group
    const checkboxes = this.formTarget.querySelectorAll(`input[data-checkbox-group="${groupName}"]`);
    
    // Check if any checkbox is selected
    const anyChecked = Array.from(checkboxes).some(cb => cb.checked);

    // Update the required attribute on the first checkbox (if any box checked, mark as not required)
    const firstCheckbox = checkboxes[0];
    if (firstCheckbox) {
      firstCheckbox.required = !anyChecked;
    }
  }

}
